/**
 * Review modal — M6 §5 "Review modal", §3 decisions.
 *
 * One upload, everything a reviewer needs to decide about it, and the two decisions: header
 * and status row, the metadata the submitter filled in, the automatic checks, a preview, a
 * comment, then *Return to submitter* or *Approve & publish*.
 *
 * **Approve is gated twice and says which gate stopped it.** A blocking check that failed and
 * a status that is not `review` are different situations with different answers — re-upload
 * versus "this was decided already" — and a greyed-out button with no `title` is the version
 * of this dialog that generates a support message. Advisory failures never gate anything
 * (`blockingOk`): they are notes, and letting `single_band` block publication would leave the
 * reviewer with no move at all.
 *
 * **Return demands a comment.** The whole point of returning an upload is telling the
 * submitter what to fix; a silent rejection is a dead end for them and a mystery for the
 * audit trail. On an *approval* the same comment is optional and becomes the version's change
 * note (§3), which is why there is one box rather than two.
 *
 * Focus, Tab-trapping and Escape are `catalogue/DatasetRecord.tsx`'s pattern, simplified: the
 * row is handed in by the queue, so this dialog always has something to render and never has
 * the "unmounts mid-refetch" problem that shaped the record's latches.
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  ApiError,
  decideUpload,
  fetchUpload,
  queryKeys,
  type UploadDecisionAction,
  type UploadChecksMeta,
  type UploadRow,
} from '../../lib/api';
import { EMDASH } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { toast } from '../../lib/toast';
import { Preview } from './Preview';
import { PreviewModal } from './PreviewModal';
import {
  blockingOk,
  REVIEW_POLL_MS,
  CHECK_GLYPH,
  checkState,
  detectedFacts,
  fmtBytes,
  previewKind,
  relativeTime,
  uploadTitle,
} from './manager';

/** Everything inside the dialog a Tab can reach, in document order (the record's list). */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The metadata rows, in the order the wizard collects them — **wire name → label key**.
 *
 * The two are not the same word everywhere: the server's allowlist stores the source
 * organisation as `source_org` (the wizard posts it under that name since Task 5's fix
 * round), while the form and its label have always called it `source`. Reading the row by the
 * form's name is how this table showed a blank where the submitter had typed "IFDC".
 * `title` is absent on purpose: it is the dialog's heading.
 */
const META_FIELDS: Array<[field: string, labelKey: string]> = [
  ['domain', 'domain'],
  ['source_org', 'source'],
  ['licence', 'licence'],
  ['cadence', 'cadence'],
  ['abstract', 'abstract'],
  ['lineage', 'lineage'],
  ['contact', 'contact'],
  ['classification', 'classification'],
];

export interface ReviewModalProps {
  /** The queue's own row — rendered immediately, then refreshed from `GET /uploads/{id}`. */
  row: UploadRow;
  onClose: () => void;
}

export function ReviewModal({ row: initial, onClose }: ReviewModalProps) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState<UploadDecisionAction | null>(null);
  /** The full-size preview dialog (client, 2026-09-19) is open over this one. */
  const [full, setFull] = useState(false);

  /**
   * The queue's row is the `initialData`, so the dialog paints on the click rather than after
   * a round trip — but it is only ever a *placeholder*, and this query must always go and ask.
   *
   * `initialDataUpdatedAt: 0` alone was not enough, and the case it missed is the common one:
   * the **wizard** fills `['upload', id]` while it polls its own upload, so by the time the
   * submitter switches to the queue and opens their row there is a cached entry — a `pending`
   * row with no checks — that `initialData` never replaces and the 5 min `staleTime` counts as
   * fresh. The reviewer then read "Pending", an empty checks list and a disabled Approve while
   * the queue behind the dialog said "In review". `refetchOnMount: 'always'` makes opening the
   * dialog a read, whatever is in the cache and whoever put it there.
   *
   * The 5 s poll is the other half: an upload opened while its checks are still running has to
   * become reviewable without the reviewer closing and reopening the dialog. It stops the
   * moment the row leaves `pending`, exactly as the queue's own poll does.
   */
  const { data } = useQuery({
    queryKey: queryKeys.upload(initial.id),
    queryFn: fetchUpload,
    initialData: initial,
    initialDataUpdatedAt: 0,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ? REVIEW_POLL_MS : false,
  });
  const row = data ?? initial;

  useEffect(() => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    overlayRef.current?.querySelector<HTMLElement>(TABBABLE)?.focus();
    return () => {
      if (openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, []);

  // Document-level, like the record's: focus can sit in the comment box, on a footer button or
  // nowhere in particular, and Escape closes the dialog from all three.
  // Escape peels one dialog at a time: the full preview first, then this one.
  useEffect(() => {
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (full) setFull(false);
      else onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, full]);

  const trapTab = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const stops = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(TABBABLE) ?? []);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (document.activeElement !== (e.shiftKey ? first : last)) return;
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }, []);

  const trimmed = comment.trim();

  const decide = async (action: UploadDecisionAction) => {
    setBusy(action);
    try {
      await decideUpload(row.id, trimmed ? { action, comment: trimmed } : { action });
      // Every status list is stale after a decision, not only the one on screen: the row has
      // just left `review` for `approved` or `rejected`.
      await qc.invalidateQueries({ queryKey: ['uploads'] });
      void qc.invalidateQueries({ queryKey: queryKeys.upload(row.id) });
      // An approve *publishes*: the catalogue list, the record and `/layers` all describe a
      // world that now has a dataset (or a version) in it. The server is moving those
      // responses to `no-cache`, which settles the HTTP layer; this settles react-query's,
      // which would otherwise serve its own 5 min copy to a reviewer who switches straight to
      // the Datasets tab or the map to check their own publish.
      void qc.invalidateQueries({ queryKey: ['datasets'] });
      void qc.invalidateQueries({ queryKey: ['datasetsAll'] });
      void qc.invalidateQueries({ queryKey: ['dataset'] });
      void qc.invalidateQueries({ queryKey: ['layers'] });
      toast(t(action === 'approve' ? 'manager.rev.approved' : 'manager.rev.returned'));
      onClose();
    } catch (err) {
      // The dialog stays open with the comment intact: a failed decision is a retry, not a
      // reason to make the reviewer type their reasoning again.
      const message = err instanceof ApiError || err instanceof Error ? err.message : '';
      toast(t('manager.rev.failed', { message }));
      setBusy(null);
    }
  };

  const canApprove = blockingOk(row) && row.status === 'review';
  const approveWhy = !blockingOk(row)
    ? t('manager.rev.blocked')
    : row.status !== 'review'
      ? t('manager.rev.notInReview')
      : undefined;

  const when = relativeTime(row.uploaded_at, i18n.language);
  const submitted = when
    ? row.uploaded_by
      ? t('manager.rev.submittedBy', { when, user: row.uploaded_by })
      : t('manager.rev.submitted', { when })
    : (row.uploaded_by ?? EMDASH);

  const facts = detectedFacts(row.checks_meta);
  const meta = META_FIELDS.map(([field, labelKey]) => {
    const raw = row.metadata?.[field];
    const value = typeof raw === 'string' ? raw.trim() : '';
    const label = t(`manager.f.${labelKey}`);
    if (field === 'classification' && value) return [label, t(`manager.cls.${value}`)];
    if (field === 'cadence' && value) return [label, t(`manager.cadence.${value}`)];
    return [label, value];
  }).filter(([, value]) => value !== '') as [string, string][];

  return (
    <div className="modal-back show" onKeyDown={trapTab}>
      <div
        className="modal mgr-review"
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mgrReviewTitle"
        data-testid="mgr-review"
      >
        <header>
          <div className="mgr-review-head">
            <h3 id="mgrReviewTitle">{uploadTitle(row)}</h3>
            <p className="mgr-review-file">
              {row.filename} · {fmtBytes(row.size_bytes)}
            </p>
          </div>
          <button type="button" className="close" aria-label={t('manager.rev.close')} onClick={onClose}>
            <Icon name="x" />
          </button>
        </header>

        <div className="mbody">
          <div className="mgr-review-status">
            <span className={`pill mgr-pill ${row.status}`} data-testid="mgr-review-pill">
              {t(`manager.status.${row.status}`)}
            </span>
            {row.kind ? (
              <span className="chip mgr-kind">
                {t(`manager.kind.${row.kind}`)}
                {row.format ? ` · ${row.format.toUpperCase()}` : ''}
              </span>
            ) : null}
            <span className="mgr-review-when">{submitted}</span>
            {/* Name *and* address: the reviewer's next act on a return is to write to this
                person, and a name alone would send them hunting through the queue for it. */}
            {row.submitter_name || row.submitter_email ? (
              <span className="mgr-review-who" data-testid="mgr-review-submitter">
                {[row.submitter_name, row.submitter_email].filter(Boolean).join(' · ')}
              </span>
            ) : null}
          </div>

          <section className="mgr-review-block">
            <h4>{t('manager.rev.meta')}</h4>
            <table className="mini" data-testid="mgr-review-meta">
              <tbody>
                {meta.map(([label, value]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    <td>{value}</td>
                  </tr>
                ))}
                {facts.map((fact) => (
                  <tr key={fact.key}>
                    <th scope="row">{t(`manager.detected.${fact.key}`)}</th>
                    <td className="mgr-review-fact">{fact.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="mgr-review-block">
            <h4>{t('manager.rev.checks')}</h4>
            {row.checks.length > 0 ? (
              <ul className="mgr-checks">
                {row.checks.map((check) => {
                  const state = checkState(check);
                  return (
                    <li key={check.name} className={state} data-testid={`mgr-check-${check.name}`}>
                      <span className="mgr-check-glyph" aria-hidden="true">
                        {CHECK_GLYPH[state]}
                      </span>
                      {/* The glyph is hidden from the accessibility tree and its three
                          states are told apart by colour, so the state has to be said in
                          words somewhere: a screen reader (and a reviewer who cannot
                          separate amber from red) otherwise hears a list of check names with
                          no verdicts on it. */}
                      <span className="sr-only">{t(`manager.checkState.${state}`)}</span>
                      {/* The server's check names are identifiers (`crs_present`); the
                          translation is the sentence a reviewer wants, and a check added on
                          the server before this dialog learns about it falls back to the
                          identifier rather than to a blank — `ReviewStep` does the same. */}
                      <span className="mgr-check-name">
                        {t(`manager.check.${check.name}`, { defaultValue: check.name })}
                      </span>
                      {check.detail ? <em>{check.detail}</em> : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mgr-hint">{t('manager.review.pending')}</p>
            )}
          </section>

          {row.checks_meta?.schema ? (
            <SchemaBlock schema={row.checks_meta.schema} />
          ) : null}

          <section className="mgr-review-block" data-testid="mgr-preview">
            <div className="mgr-review-blockhead">
              <h4>{t('manager.rev.preview')}</h4>
              {previewKind(row) ? (
                <button
                  type="button"
                  className="btn ghost sm"
                  data-testid="mgr-preview-open"
                  onClick={() => setFull(true)}
                >
                  {t('manager.rev.openFull')}
                </button>
              ) : null}
            </div>
            <Preview row={row} />
          </section>

          {row.review_comment ? (
            <section className="mgr-review-block">
              <h4>{t('manager.rev.lastComment')}</h4>
              <p className="mgr-hint">{row.review_comment}</p>
            </section>
          ) : null}

          <section className="mgr-review-block">
            <label className="mgr-label" htmlFor="mgrComment">
              {t('manager.rev.comment')}
            </label>
            <textarea
              id="mgrComment"
              className="mgr-input mgr-textarea"
              rows={3}
              data-testid="mgr-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <p className="mgr-hint">{t('manager.rev.commentHint')}</p>
          </section>
        </div>

        <footer>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('manager.rev.close')}
          </button>
          <button
            type="button"
            className="btn ghost mgr-danger"
            data-testid="mgr-return"
            disabled={trimmed === '' || busy !== null}
            title={trimmed === '' ? t('manager.rev.needComment') : undefined}
            onClick={() => void decide('return')}
          >
            {busy === 'return' ? t('manager.rev.returning') : t('manager.rev.return')}
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="mgr-approve"
            disabled={!canApprove || busy !== null}
            title={approveWhy}
            onClick={() => void decide('approve')}
          >
            {busy === 'approve' ? t('manager.rev.approving') : t('manager.rev.approve')}
          </button>
        </footer>
      </div>
      {full ? <PreviewModal row={row} onClose={() => setFull(false)} /> : null}
    </div>
  );
}

/**
 * The column diff of a new version (client, 2026-09-19): what the new file adds, drops,
 * appears to rename, and keeps, against the version the catalogue serves today. Renames are
 * the server's guesses and are labelled as such; nothing here changes what publish does.
 */
function SchemaBlock({ schema }: { schema: NonNullable<UploadChecksMeta['schema']> }) {
  const { t } = useTranslation();
  const dependents = schema.dependents ?? [];
  return (
    <section className="mgr-review-block" data-testid="mgr-schema">
      <h4>{t('manager.schema.title')}</h4>
      {schema.changed ? (
        <>
          <ul className="mgr-schema-list">
            {schema.added.map((name) => (
              <li key={`a-${name}`} className="add" data-testid="mgr-schema-added">
                <span className="mgr-schema-tag">{t('manager.schema.added')}</span>
                <code>{name}</code>
              </li>
            ))}
            {schema.removed.map((name) => (
              <li key={`r-${name}`} className="del" data-testid="mgr-schema-removed">
                <span className="mgr-schema-tag">{t('manager.schema.removed')}</span>
                <code>{name}</code>
              </li>
            ))}
            {schema.renamed.map((r) => (
              <li key={`n-${r.from}`} className="ren" data-testid="mgr-schema-renamed">
                <span className="mgr-schema-tag">{t('manager.schema.renamed')}</span>
                <code>{r.from}</code> <span aria-hidden="true">→</span>{' '}
                <span className="sr-only">{t('manager.schema.becomes')}</span>
                <code>{r.to}</code>
              </li>
            ))}
          </ul>
          <p className="mgr-hint" data-testid="mgr-schema-kept">
            {t('manager.schema.kept', { n: schema.kept.length, total: schema.previous.length })}
            {schema.renamed.length > 0 ? ` ${t('manager.schema.guess')}` : ''}
          </p>
          {dependents.length > 0 ? (
            <p className="mgr-schema-warn" data-testid="mgr-schema-dependents">
              {t('manager.schema.dependents', { columns: dependents.join(', ') })}
            </p>
          ) : null}
        </>
      ) : (
        <p className="mgr-hint" data-testid="mgr-schema-same">
          {t('manager.schema.same', { n: schema.previous.length })}
        </p>
      )}
    </section>
  );
}

export default ReviewModal;
